<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import KanbanReportWidget from 'kanban/components/reports/KanbanReportWidget.vue';
import {
  formatNumber,
  formatPercent,
  formatCurrencyValue,
  formatDimensionGroupName,
} from 'kanban/helper/reportFormatters';
import {
  BUILT_IN_DIMENSIONS,
  CUSTOM_ATTRIBUTE_DIMENSION,
} from 'kanban/constants/reports';

const props = defineProps({
  data: {
    type: Object,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: Boolean,
    default: false,
  },
  locale: {
    type: String,
    default: 'en',
  },
});

const emit = defineEmits(['retry']);

const { t } = useI18n();

// Os eixos embutidos chegam do servidor com a chave no lugar do nome, porque quem traduz é
// a tela. Um eixo por etiqueta já vem com o nome que a conta deu a ele.
const dimensionLabel = computed(() => {
  const key = props.data?.dimension?.key;
  if (BUILT_IN_DIMENSIONS.includes(key)) {
    return t(`KANBAN.REPORTS.DIMENSION.BUILT_IN.${key.toUpperCase()}`);
  }
  return props.data?.dimension?.name || t('KANBAN.REPORTS.DIMENSION.VALUE');
});

// No eixo de motivo de perda só existe card perdido, então ganho, conversão, receita,
// ticket e ciclo seriam cinco colunas zeradas. A tabela mostra só o que tem conteúdo.
const isLostReasonAxis = computed(
  () => props.data?.dimension?.type === 'lost_reason'
);

const rows = computed(() => props.data?.rows || []);
const totals = computed(() => props.data?.totals || null);
const currency = computed(() => props.data?.currency || 'USD');

// "Não informado", "Outros", meses e faixas de um atributo saem no idioma de quem lê.
const rowName = row =>
  formatDimensionGroupName(row, props.data?.dimension, props.locale, t);

// Um atributo de data agrupa os cartões criados, ganhos ou perdidos no período, não a carteira toda.
const isDateAttributeAxis = computed(
  () =>
    props.data?.dimension?.type === CUSTOM_ATTRIBUTE_DIMENSION &&
    props.data?.dimension?.display_type === 'date'
);

// The bar is relative to the best-converting row, so the column reads as a ranking even
// when every rate is small.
const bestRate = computed(() =>
  rows.value.reduce((max, row) => Math.max(max, row.win_rate || 0), 0)
);
const barWidth = row =>
  bestRate.value > 0
    ? `${Math.round(((row.win_rate || 0) / bestRate.value) * 100)}%`
    : '0%';

const rateLabel = row =>
  row.win_rate === null || row.win_rate === undefined
    ? t('KANBAN.REPORTS.SUMMARY.WIN_RATE_NA')
    : formatPercent(row.win_rate, props.locale);

const money = value =>
  value === null || value === undefined
    ? t('KANBAN.REPORTS.NONE')
    : formatCurrencyValue(value, currency.value, props.locale, {
        compact: true,
      });

const cycleLabel = row =>
  row.avg_cycle_days === null || row.avg_cycle_days === undefined
    ? t('KANBAN.REPORTS.NONE')
    : t('KANBAN.REPORTS.DIMENSION.DAYS', {
        count: formatNumber(row.avg_cycle_days, props.locale),
      });

// Won deals against the same-length window right before the current one. Null when there
// is nothing to compare against, so the cell stays empty instead of showing a fake +100%.
const trend = row => {
  const previous = row.won_previous;
  if (!previous) return null;
  const delta = row.won - previous;
  if (delta === 0) return null;
  return { label: delta > 0 ? `+${delta}` : String(delta), isUp: delta > 0 };
};
</script>

<template>
  <KanbanReportWidget
    :title="t('KANBAN.REPORTS.DIMENSION.TITLE')"
    :info-text="t('KANBAN.REPORTS.DIMENSION.INFO')"
    :is-loading="isLoading"
    :error="error"
    :is-empty="!rows.length"
    :empty-text="t('KANBAN.REPORTS.DIMENSION.EMPTY')"
    @retry="emit('retry')"
  >
    <div class="overflow-x-auto">
      <table class="w-full text-sm">
        <thead>
          <tr class="text-left text-xs font-medium text-n-slate-10">
            <th class="py-2 pr-4 font-medium">
              {{ dimensionLabel }}
            </th>
            <th
              v-if="!isLostReasonAxis"
              class="py-2 px-4 font-medium text-right"
            >
              {{ t('KANBAN.REPORTS.DIMENSION.WON') }}
            </th>
            <th class="py-2 px-4 font-medium text-right">
              {{ t('KANBAN.REPORTS.DIMENSION.LOST') }}
            </th>
            <th
              v-if="!isLostReasonAxis"
              class="py-2 px-4 font-medium text-right min-w-[9rem]"
            >
              {{ t('KANBAN.REPORTS.DIMENSION.WIN_RATE') }}
            </th>
            <th
              v-if="!isLostReasonAxis"
              class="py-2 px-4 font-medium text-right"
            >
              {{ t('KANBAN.REPORTS.DIMENSION.REVENUE') }}
            </th>
            <th
              v-if="!isLostReasonAxis"
              class="py-2 px-4 font-medium text-right"
            >
              {{ t('KANBAN.REPORTS.DIMENSION.AVG_TICKET') }}
            </th>
            <th
              v-if="!isLostReasonAxis"
              class="py-2 pl-4 font-medium text-right"
            >
              {{ t('KANBAN.REPORTS.DIMENSION.CYCLE') }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="row in rows"
            :key="row.key || 'unclassified'"
            class="border-t border-n-slate-3"
          >
            <td class="py-2 pr-4">
              <span
                class="truncate text-n-slate-12"
                :class="{ 'italic text-n-slate-11': !row.key }"
              >
                {{ rowName(row) }}
              </span>
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 px-4 text-right tabular-nums text-n-teal-11"
            >
              <span class="inline-flex items-center gap-1 justify-end">
                {{ formatNumber(row.won, locale) }}
                <span
                  v-if="trend(row)"
                  class="text-xs"
                  :class="trend(row).isUp ? 'text-n-teal-11' : 'text-n-ruby-11'"
                >
                  {{ trend(row).label }}
                </span>
              </span>
            </td>
            <td class="py-2 px-4 text-right tabular-nums text-n-ruby-11">
              {{ formatNumber(row.lost, locale) }}
            </td>
            <td v-if="!isLostReasonAxis" class="py-2 px-4">
              <div class="flex items-center gap-2 justify-end">
                <div
                  class="hidden sm:block h-1.5 w-20 rounded-full bg-n-slate-4 overflow-hidden"
                >
                  <div
                    class="h-full rounded-full bg-n-blue-9"
                    :style="{ width: barWidth(row) }"
                  />
                </div>
                <span class="tabular-nums text-n-slate-12 w-12 text-right">
                  {{ rateLabel(row) }}
                </span>
              </div>
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 px-4 text-right tabular-nums text-n-slate-12"
            >
              {{ money(row.revenue) }}
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 px-4 text-right tabular-nums text-n-slate-12"
            >
              {{ money(row.avg_ticket) }}
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 pl-4 text-right tabular-nums text-n-slate-11"
            >
              {{ cycleLabel(row) }}
            </td>
          </tr>
        </tbody>
        <tfoot v-if="totals">
          <tr class="border-t border-n-slate-4 font-medium">
            <td class="py-2 pr-4 text-n-slate-11">
              {{ t('KANBAN.REPORTS.DIMENSION.TOTAL') }}
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 px-4 text-right tabular-nums text-n-slate-12"
            >
              {{ formatNumber(totals.won, locale) }}
            </td>
            <td class="py-2 px-4 text-right tabular-nums text-n-slate-12">
              {{ formatNumber(totals.lost, locale) }}
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 px-4 text-right tabular-nums text-n-slate-12"
            >
              {{ rateLabel(totals) }}
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 px-4 text-right tabular-nums text-n-slate-12"
            >
              {{ money(totals.revenue) }}
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 px-4 text-right tabular-nums text-n-slate-12"
            >
              {{ money(totals.avg_ticket) }}
            </td>
            <td
              v-if="!isLostReasonAxis"
              class="py-2 pl-4 text-right tabular-nums text-n-slate-11"
            >
              {{ cycleLabel(totals) }}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>

    <p v-if="data?.multi_valued" class="text-xs text-n-slate-10">
      {{ t('KANBAN.REPORTS.DIMENSION.MULTI_VALUED_NOTE') }}
    </p>
    <p v-if="isDateAttributeAxis" class="text-xs text-n-slate-10">
      {{ t('KANBAN.REPORTS.DIMENSION.ATTRIBUTE_PERIOD_NOTE') }}
    </p>
  </KanbanReportWidget>
</template>
